import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, Plus, Phone, User, Mail, Building2 } from "lucide-react";
import { api, media } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth, can } from "@/lib/auth";
import { fmtPrice, vTitle, fmtDate } from "@/lib/format";
import { PageTitle, Loader, Empty, Section, Field, RestrictionToggles, StatusBadge } from "@/components/xd/ui";
import { SpecsForm, EMPTY_SPECS, specsPayload } from "@/components/xd/VehicleForm";
import { ReviewTag } from "@/pages/dealer/Dealer";
import { useAct } from "./Admin";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const RESTR = { facebook: true, instagram: true, tiktok: true, telegram: true, yad2: true };
const num = (x) => (x === "" || x == null ? null : Number(x));

export const DealerSelect = ({ value, onChange, testid = "dealer-select" }) => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/dealers-lite").then((r) => setItems(r.data.items)).catch(() => {}); }, []);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="xd-input" data-testid={testid}><SelectValue placeholder={t("mgmt.choose_dealer")} /></SelectTrigger>
      <SelectContent>{items.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`dealer-option-${d.uid}`}>{d.label}</SelectItem>)}</SelectContent>
    </Select>
  );
};

const NewConf = ({ conf, setConf }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const fin = can(user, "financial.view");
  const cont = can(user, "dealers.confidential");
  if (!fin && !cont) return null;
  const f = (k, type) => <Field label={t(`conf.${k}`)}><Input type={type} dir="ltr" className="xd-input" value={conf[k]} onChange={(e) => setConf({ ...conf, [k]: e.target.value })} data-testid={`new-conf-${k}-input`} /></Field>;
  return (
    <Section title={t("dealer.confidential")} testid="admin-new-conf-section">
      <p className="text-xs text-zinc-500">{t("mgmt.conf_optional")}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {fin && <>{f("asking_price", "number")}{f("final_price", "number")}</>}
        {cont && <>{f("contact_name", "text")}{f("contact_phone", "tel")}</>}
      </div>
    </Section>
  );
};

export function AdminVehicleNew() {
  const { t } = useLang();
  const { user } = useAuth();
  const act = useAct();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [dealerId, setDealerId] = useState(sp.get("dealer") || "");
  const [specs, setSpecs] = useState(EMPTY_SPECS);
  const [restr, setRestr] = useState(RESTR);
  const [conf, setConf] = useState({ asking_price: "", final_price: "", contact_name: "", contact_phone: "" });
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const c = { asking_price: num(conf.asking_price), final_price: num(conf.final_price), contact_name: conf.contact_name || null, contact_phone: conf.contact_phone || null };
    const body = { dealer_id: dealerId, specs: specsPayload(specs), restrictions: restr, category_ids: [], conf: Object.values(c).some((x) => x != null) ? c : null, advertised_price: num(price) };
    const r = await act(() => api.post("/admin/vehicles", body));
    setBusy(false);
    if (r) navigate(`/admin/vehicles/${r.data.id}`, { replace: true });
  };
  return (
    <form onSubmit={save} className="space-y-6" data-testid="admin-new-vehicle-form">
      <Link to="/admin/vehicles" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white"><ArrowRight size={16} />{t("admin.vehicles")}</Link>
      <PageTitle eyebrow={t("admin.catalog")} title={t("mgmt.new_vehicle")} />
      <Section title={t("mgmt.dealer_link")} testid="admin-new-dealer-section">
        <p className="text-xs text-zinc-500">{t("mgmt.dealer_link_hint")}</p>
        <div className="max-w-md"><DealerSelect value={dealerId} onChange={setDealerId} testid="admin-new-dealer-select" /></div>
      </Section>
      <Section title={t("dealer.specs")}><SpecsForm value={specs} onChange={setSpecs} /></Section>
      <NewConf conf={conf} setConf={setConf} />
      {can(user, "prices.set") && (
        <Section title={t("price.c_title")}>
          <p className="text-xs text-zinc-500">{t("price.c_admin_hint")}</p>
          <Input type="number" dir="ltr" className="xd-input w-full sm:w-56" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="admin-new-advertised-price" />
        </Section>
      )}
      <Section title={t("broker.restrictions")}><RestrictionToggles value={restr} onChange={setRestr} /></Section>
      <p className="text-xs text-zinc-500">{t("mgmt.after_create")}</p>
      <button disabled={busy || !dealerId} className="btn-xd" data-testid="admin-new-vehicle-submit">{t("dealer.create_continue")}</button>
    </form>
  );
}

const Info = ({ icon, label, value, testid }) => (
  <div className="xd-panel p-4 min-w-0" data-testid={testid}>
    <p className="text-xs text-zinc-500 flex items-center gap-2">{icon}{label}</p>
    <p className="mt-1.5 text-sm break-words">{value || "—"}</p>
  </div>
);

const PriceTrio = ({ v }) => {
  const { t } = useLang();
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" dir="ltr">
      {v.asking_price !== undefined && <span className="text-zinc-400 ltr-num px-1.5 py-0.5 border border-white/10">A {fmtPrice(v.asking_price)}</span>}
      {v.final_price !== undefined && <span className="text-amber-300 ltr-num px-1.5 py-0.5 border border-amber-500/30">B {fmtPrice(v.final_price)}</span>}
      <span className="text-white ltr-num px-1.5 py-0.5 border border-[var(--xd-red)]/50">C {fmtPrice(v.price)}</span>
      <span className="sr-only">{t("price.c_title")}</span>
    </div>
  );
};

export const AdminVehicleRow = ({ v }) => {
  const { t, lang } = useLang();
  return (
    <Link to={`/admin/vehicles/${v.id}`} className="xd-card p-3 flex items-center gap-4 min-w-0" data-testid={`admin-vehicle-row-${v.code}`}>
      <div className="relative w-20 h-14 sm:w-24 sm:h-16 shrink-0 bg-[#111] overflow-hidden">
        {v.cover && <img src={media(v.cover, "thumb")} alt="" className="h-full w-full object-cover" />}
        <StatusBadge status={v.availability} className="absolute top-1 start-1 !text-[10px] !px-1.5 !py-0.5" />
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <p className="line-clamp-2 break-words text-sm sm:text-base">{vTitle(v, lang)}</p>
        <p className="text-xs text-zinc-500 ltr-num">{v.code} · {v.dealer_uid}{v.is_demo && <span className="ms-2 text-amber-400">DEMO</span>}</p>
        <p className="text-xs text-zinc-500"><ReviewTag s={v.review_status} /> · {v.published ? t("dealer.live") : t("dealer.not_live")}{v.price_alert && <span className="text-[var(--xd-red)]"> · {t("alert.price_changed")}</span>}</p>
        <PriceTrio v={v} />
      </div>
    </Link>
  );
};

export function AdminDealer() {
  const { id } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/dealers/${id}`).then((r) => setD(r.data)).catch(() => navigate("/admin/dealers")); }, [id, navigate]);
  if (!d) return <Loader />;
  const u = d.dealer;
  return (
    <div className="space-y-6" data-testid="admin-dealer-profile">
      <Link to="/admin/dealers" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white" data-testid="dealer-profile-back"><ArrowRight size={16} />{t("admin.dealers")}</Link>
      <PageTitle eyebrow={`${u.uid || "—"} · ${t(`ustatus.${u.status}`)}`} title={u.business_name || u.uid}>
        {can(user, "vehicles.manage") && u.status === "approved" && <Link to={`/admin/vehicles/new?dealer=${u.id}`} className="btn-xd" data-testid="dealer-profile-add-vehicle"><Plus size={16} />{t("mgmt.add_for_dealer")}</Link>}
      </PageTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Info icon={<Building2 size={13} />} label={t("form.business_name")} value={u.business_name} testid="dealer-info-business" />
        <Info icon={<User size={13} />} label={t("profile.contact_person")} value={u.contact_person || u.name} testid="dealer-info-contact" />
        <Info icon={<Phone size={13} />} label={t("conf.contact_phone")} value={<span className="ltr-num">{u.contact_phone || u.phone}</span>} testid="dealer-info-contact-phone" />
        <Info icon={<Phone size={13} />} label={t("profile.account_phone")} value={<span className="ltr-num">{u.phone}</span>} testid="dealer-info-phone" />
        <Info icon={<Mail size={13} />} label={t("form.email")} value={<span className="ltr-num">{u.email}</span>} testid="dealer-info-email" />
        <Info label={t("profile.joined")} value={<span className="ltr-num">{fmtDate(u.approved_at || u.created_at)}</span>} testid="dealer-info-joined" />
        <Info label={t("guide.status")} value={u.guide_accepted_at ? <span className="ltr-num text-emerald-300">{t("guide.accepted_on")} {fmtDate(u.guide_accepted_at)} · v{u.guide_version}</span> : t("guide.not_accepted")} testid="dealer-info-guide" />
      </div>
      <Section title={`${t("mgmt.dealer_vehicles")} (${d.vehicles.length})`} testid="dealer-profile-vehicles">
        {!d.vehicles.length ? <Empty text={t("common.none")} /> : <div className="grid gap-3">{d.vehicles.map((v) => <AdminVehicleRow key={v.id} v={v} />)}</div>}
      </Section>
    </div>
  );
}
